import { useState } from "react";
import { MapPin, Phone, Mail, MessageCircle, Clock } from "lucide-react";
import { toast } from "sonner";
import { Reveal, SectionLabel } from "./Reveal";
import api from "@/lib/api";

export default function Contact({ site }) {
  const c = site?.contact || {};
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.message) {
      toast.error("নাম ও বার্তা দিন");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/public/submit", { type: "contact", data: form });
      toast.success(data.message);
      setForm({ name: "", email: "", message: "" });
    } catch {
      toast.error("দুঃখিত, আবার চেষ্টা করুন।");
    } finally {
      setBusy(false);
    }
  };

  const info = [
    { icon: MapPin, label: "ঠিকানা", value: c.address },
    { icon: Phone, label: "ফোন", value: c.phone },
    { icon: Mail, label: "ইমেইল", value: c.email },
    { icon: Clock, label: "অফিস সময়", value: c.hours },
  ].filter((x) => x.value);

  return (
    <section id="contact" className="py-24 lg:py-32 bg-[var(--c-bg-alt)]">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <Reveal className="text-center max-w-3xl mx-auto">
          <SectionLabel>{c.label}</SectionLabel>
          <h2 className="font-heading font-bold text-[var(--c-primary)] mt-5 text-3xl sm:text-4xl lg:text-5xl">{c.title}</h2>
          <p className="font-body text-[var(--c-muted)] mt-5 text-base sm:text-lg">{c.subtitle}</p>
        </Reveal>

        <div className="grid lg:grid-cols-2 gap-10 mt-14">
          <Reveal>
            <div className="space-y-5">
              {info.map((it, i) => (
                <div key={i} className="flex items-start gap-4 bg-[var(--c-card)] border border-[var(--c-border)] rounded-2xl p-5">
                  <span className="grid place-items-center h-12 w-12 rounded-full bg-[var(--c-primary)]/5 text-[var(--c-primary)]">
                    <it.icon className="h-5 w-5" strokeWidth={1.5} />
                  </span>
                  <div>
                    <div className="font-body text-xs uppercase tracking-widest text-[var(--c-secondary)]">{it.label}</div>
                    <div className="font-heading text-lg text-[var(--c-text)] mt-1">{it.value}</div>
                  </div>
                </div>
              ))}
              {c.mapEmbed && (
                <div className="rounded-2xl overflow-hidden border border-[var(--c-border)] h-56">
                  <iframe title="map" src={c.mapEmbed} className="w-full h-full" loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
                </div>
              )}
            </div>
          </Reveal>

          <Reveal delay={0.15}>
            <form data-testid="contact-form" onSubmit={submit} className="bg-[var(--c-card)] rounded-3xl border border-[var(--c-border)] p-8 shadow-lg">
              <h3 className="font-heading text-2xl font-semibold text-[var(--c-primary)]">{c.formTitle}</h3>
              <div className="space-y-4 mt-6">
                <input data-testid="contact-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="আপনার নাম" className="w-full rounded-xl border border-[var(--c-border)] bg-[var(--c-bg)] px-4 py-3 font-body focus:outline-none focus:border-[var(--c-secondary)]" />
                <input data-testid="contact-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="ইমেইল বা ফোন" className="w-full rounded-xl border border-[var(--c-border)] bg-[var(--c-bg)] px-4 py-3 font-body focus:outline-none focus:border-[var(--c-secondary)]" />
                <textarea data-testid="contact-message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="আপনার বার্তা" rows={5} className="w-full rounded-xl border border-[var(--c-border)] bg-[var(--c-bg)] px-4 py-3 font-body resize-none focus:outline-none focus:border-[var(--c-secondary)]" />
                <button data-testid="contact-submit" disabled={busy} className="w-full rounded-full bg-[var(--c-primary)] text-white font-body font-medium py-3.5 hover:bg-[var(--c-secondary)] transition-colors disabled:opacity-60">
                  {busy ? "পাঠানো হচ্ছে..." : c.submitLabel}
                </button>
              </div>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
